import {
  AdsDataState,
  MarketDataState,
  SoldDataState,
} from '@/context/FilterContext';
import {
  formatDateToString,
  getHomeTypeApiParams,
  getRefApiParams,
  getWeightApiParams,
  removeNullApiParams,
} from '@/helpers';
import { resetAdsFilterParams } from '@/context/AdsDefault';
import { IndexResponseType } from '@/types/apiTypes';
import { AdsSearchParamsCriteriaType, SoldSearchParamsCriteriaType } from '@/types/apiTypes';
const getSoldApiParams = (soldData: SoldDataState): SoldSearchParamsCriteriaType => {
  const homeType = getHomeTypeApiParams(soldData.filterData.home_type);

  const filterApiParams = {
    contract_date_min:
      soldData.filterData.contract_date_min &&
      formatDateToString(soldData.filterData.contract_date_min),
    contract_date_max:
      soldData.filterData.contract_date_max &&
      formatDateToString(soldData.filterData.contract_date_max),
    construction_year_min: soldData.filterData.construction_year_min,
    construction_year_max: soldData.filterData.construction_year_max,
    distance_center: soldData.filterData.distance_center,
    home_type: homeType,
    ownership_type: soldData.filterData.ownership_type,
    shore_distance_min: soldData.filterData.shore_distance_min,
    shore_distance_max: soldData.filterData.shore_distance_max,
    living_area_min: soldData.filterData.living_area_min,
    living_area_max: soldData.filterData.living_area_max,
    monthly_fee_min: soldData.filterData.monthly_fee_min,
    monthly_fee_max: soldData.filterData.monthly_fee_max,
    price_min:
      soldData.filterData.price_min && soldData.filterData.price_min * 1000000,
    price_max:
      soldData.filterData.price_max && soldData.filterData.price_max * 1000000,
    rooms_min: soldData.filterData.rooms_min,
    rooms_max: soldData.filterData.rooms_max,
    owner_id: soldData.filterData.owner_id,
    limit: soldData.filterData.limit,
    error_code: soldData.filterData.error_code,
    is_close_to_population_centre:
      soldData.filterData.is_close_to_population_centre,
    is_new_construction: soldData.filterData.is_new_construction,
    firms: soldData.filterData.estate_agencies?.length ? [soldData.filterData.estate_agencies] : null,
    floor_min: soldData.filterData.floor_min,
    floor_max: soldData.filterData.floor_max,
  };

  const cleanFiltedApiParams = removeNullApiParams(filterApiParams);

  return {
    ...getRefApiParams(soldData.refData),
    ...getWeightApiParams(soldData.weightData),
    ...cleanFiltedApiParams,
  };
};

const getMarketApiParams = (marketData: MarketDataState) => {
  const homeType = getHomeTypeApiParams(marketData.filterData.home_type);

  const filterApiParams = {
    ancillary_area_min: marketData.filterData.ancillary_area_min,
    ancillary_area_max: marketData.filterData.ancillary_area_max,
    construction_year_min: marketData.filterData.construction_year_min,
    construction_year_max: marketData.filterData.construction_year_max,
    living_area_min: marketData.filterData.living_area_min,
    living_area_max: marketData.filterData.living_area_max,
    monthly_fee_min: marketData.filterData.monthly_fee_min,
    monthly_fee_max: marketData.filterData.monthly_fee_max,
    home_type: homeType,
    ownership_type: marketData.filterData.ownership_type,
    price_min:
      marketData.filterData.price_min &&
      marketData.filterData.price_min * 1000000,
    price_max:
      marketData.filterData.price_max &&
      marketData.filterData.price_max * 1000000,
    rooms_min: marketData.filterData.rooms_min,
    rooms_max: marketData.filterData.rooms_max,
    registered_date_min:
      marketData.filterData.registered_date_min &&
      formatDateToString(marketData.filterData.registered_date_min),
    registered_date_max:
      marketData.filterData.registered_date_max &&
      formatDateToString(marketData.filterData.registered_date_max),
  };

  const cleanFiltedApiParams = removeNullApiParams(filterApiParams);

  return {
    ...getRefApiParams(marketData.refData),
    ...cleanFiltedApiParams,
  };
};

const getAdsApiParams = (adsData: AdsDataState): AdsSearchParamsCriteriaType => {
  const homeType = getHomeTypeApiParams(adsData.filterData.home_type);
  const filterApiParams = {
    advertising_days_min: adsData.filterData.ad_advertising_days_min || resetAdsFilterParams.ad_advertising_days_min,
    advertising_days_max: adsData.filterData.ad_advertising_days_max || resetAdsFilterParams.ad_advertising_days_max,
    construction_year_min: adsData.filterData.construction_year_min,
    construction_year_max: adsData.filterData.construction_year_max,
    error_code: adsData.filterData.error_code,
    home_type: homeType,
    is_close_to_population_centre: true,
    limit: adsData.filterData.limit,
    living_area_min: adsData.filterData.living_area_min,
    living_area_max: adsData.filterData.living_area_max,
    price_min:
      adsData.filterData.price_min && adsData.filterData.price_min * 1000000,
    price_max:
      adsData.filterData.price_max && adsData.filterData.price_max * 1000000,
    rooms_min: adsData.filterData.rooms_min,
    rooms_max: adsData.filterData.rooms_max,
    ownership_type: adsData.filterData.ownership_type,
    active: 1,
    registered_date_min:
      adsData.filterData.registered_date_min &&
      formatDateToString(adsData.filterData.registered_date_min),
    registered_date_max:
      adsData.filterData.registered_date_max &&
      formatDateToString(adsData.filterData.registered_date_max),
  };

  const cleanFiltedApiParams = removeNullApiParams(filterApiParams);

  return {
    ...getRefApiParams(adsData.refData),
    ...getWeightApiParams(adsData.weightData),
    ...cleanFiltedApiParams,
  };
};

const getIndexParametersAsQueryParams = (
  internalIndexValues: IndexResponseType
) => {
  const parametersObj = {
    id: internalIndexValues.index_id.toString(),
    ...(internalIndexValues.reference_date && {
      reference_date: internalIndexValues.reference_date,
    }),
    ...(internalIndexValues.reference_value && {
      reference_value: Math.floor(
        internalIndexValues.reference_value
      ).toString(),
    }),
  };

  const parameters = new URLSearchParams(parametersObj).toString();

  return parameters;
};

const getTableIdsAsArray = (tableIds: Set<string> | null) =>
  tableIds?.size ? Array.from(tableIds) : [];

const getPolygonApiData = (coordinates: number[][] | null) => {
  if (!coordinates) return null;
  return {
    features: [
      {
        geometry: {
          coordinates: [[coordinates]],
          crs: {
            properties: {
              name: 'EPSG:4326',
            },
            type: 'name',
          },
          type: 'MultiPolygon',
        },
        type: 'Feature',
      },
    ],
    type: 'FeatureCollection',
  };
};

export {
  getSoldApiParams,
  getMarketApiParams,
  getAdsApiParams,
  getIndexParametersAsQueryParams,
  getPolygonApiData,
  getTableIdsAsArray,
};
